import React, { useState, useRef, useEffect } from 'react';
import Header from './Header.jsx';
import { domain } from './Domain.jsx';
import notify from './Nots.jsx';
import useFetch from './useFetch.jsx';
import useApiRequest from './useApiRequest.jsx';
import { useNavigate } from 'react-router-dom';
import { useLoadingContext } from './LoadingContext.jsx';
import { saveToLocalStorage } from './EncryptDecrypt.jsx';
import { Button, Modal } from 'react-bootstrap';
import PaymentDetailsForm from './PaymentDetailsForm.jsx';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    DollarSign, CreditCard, Building2, TrendingUp, Calendar, 
    Users, Eye, Edit, Trash2, Plus, ArrowRight, 
    CheckCircle, Clock, AlertCircle, Wallet
} from 'lucide-react';

const RevenueDashboard = () => {
    const { getRequest, postRequest, putRequest } = useApiRequest();
    const { setLoading } = useLoadingContext();
    const [accountView, setAccountView] = useState(false);
    const { data, loading, setLoading: setLoadingData, refresh, setRefresh } = useFetch(`${domain}/my-event-earnings`);
    const [totalAmount, setTotalAmount] = useState(0);
    const [event, setEvent] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [paymentRequestAmount, setPaymentRequestAmount] = useState(0);
    const [userBalance, setUserBalance] = useState(0);
    const [paymentDetails, setPaymentDetails] = useState(null);
    const [myAccounts, setMyAccounts] = useState([]);
    const [editing, setEditing] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6;

    useEffect(() => {
        const total = data && data.reduce((sum, event) => sum + event.amount, 0);
        setTotalAmount(total);
    }, [data]);

    const initRequest = async (item) => {
        if (item.amount <= 0) {
            notify("less money to push request", "error");
        } else {
            setShowModal(true);
            setUserBalance(item.amount - item.total_paid);
            setEvent(item);
        }
    };

    const handleRequest = async () => {
        if (paymentRequestAmount > userBalance) {
            notify("Your request for payment is way above what you collected", "error");
            return;
        }
        setLoading(true);
        setShowModal(false);
        const formData = new FormData();
        formData.append("event", event.event['eventId']);
        formData.append("amount", paymentRequestAmount);
        formData.append("total_available", event.amount);
        formData.append("total_paid", event.total_paid);
        const response = await postRequest(`${domain}/payment-requests`, formData, true);
        if (response) {
            notify(JSON.stringify(response['body']['message']), response['body']['status'] === "success" ? "info" : "error");
            setLoading(false);
            setUserBalance(0);
            setPaymentRequestAmount(0);
            setEvent(null);
            setRefresh(!refresh);
        }
    };

    const handleViewAccount = async (status) => {
        if (status === false) {
            setAccountView(true);
            const response = await getRequest(`${domain}/payment-details?q=details`);
            if (response.status === 200) {
                setMyAccounts(response.body.message);
            } else {
                notify(response.body.message);
            }
        } else {
            setAccountView(false);
        }
    };

    const handleSubmit = async (data) => {
        const formData = new FormData();
        for (const [key, value] of Object.entries(data)) {
            formData.append(key, value);
        }
        setLoading(true);
        let response;
        if (editing) {
            response = await putRequest(`${domain}/payment-details/${editing}`, formData, true);
        } else {
            response = await postRequest(`${domain}/payment-details`, formData, true);
        }
        if (response.status === 201 || response.status === 202) {
            notify(JSON.stringify(response.body.data), "info");
            setPaymentDetails(response.body.data);
            const found = myAccounts.findIndex(item => parseInt(item.id) === parseInt(editing));
            if (found !== -1) {
                myAccounts[found] = response.body.data;
                setMyAccounts([...myAccounts]);
            }
        } else {
            notify(JSON.stringify(response.data), "error");
        }
        setLoading(false);
    };

    const editAccount = (id) => {
        const selectedAccount = myAccounts.find(item => parseInt(item.id) === parseInt(id));
        if (selectedAccount) {
            setPaymentDetails(selectedAccount);
            setEditing(id);
        }
    };

    const onCancel = () => {
        setEditing(null);
        setPaymentDetails(null);
    };

    // Calculate pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentEvents = data?.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(data?.length / itemsPerPage);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const MemoizedPaymentDetailsForm = React.memo(PaymentDetailsForm);

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-orange-50 to-teal-50">
            <Header />
            
            <div className="container mx-auto px-4 py-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="max-w-7xl mx-auto"
                >
                    {/* Header Section */}
                    <div className="mb-8">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                            <div>
                                <h1 className="text-lg font-bold text-gray-800 mb-2">
                                    {accountView ? "Account Management" : "Revenue Dashboard"}
                                </h1>
                                <p className="text-sm text-gray-600">
                                    {accountView 
                                        ? "Manage your payment accounts and banking information"
                                        : "Track your event earnings and payment requests"
                                    }
                                </p>
                            </div>
                            
                            {/* Quick Stats */}
                            {!accountView && (
                                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl flex items-center justify-center">
                                            <DollarSign className="w-6 h-6 !text-white" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">Total Revenue</p>
                                            <p className="text-sm font-bold text-orange-600">
                                                Ugx {totalAmount && totalAmount.toLocaleString()}/=
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                        {/* Main Content Area */}
                        <div className="lg:col-span-3">
                            <AnimatePresence mode="wait">
                    {accountView ? (
                                    <motion.div
                                        key="account-view"
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8"
                                    >
                                        <div className="flex items-center gap-4 mb-8">
                                            <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-teal-500 rounded-xl flex items-center justify-center">
                                                <CreditCard className="w-6 h-6 !text-white" />
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-bold text-gray-800">Payment Accounts</h3>
                                                <p className="text-sm text-gray-600">Manage your bank and mobile money accounts</p>
                                            </div>
                                        </div>

                                        {/* Account Buttons */}
                                        <div className="mb-8">
                                            <h4 className="text-lg font-semibold text-gray-800 mb-4">Your Accounts</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {myAccounts.length > 0 && myAccounts.map((account) => (
                                                    <motion.button
                                                        key={account.id}
                                                        onClick={() => editAccount(account.id)}
                                                        className={`p-4 rounded-xl border-2 transition-all duration-200 hover:shadow-lg ${
                                                            account.account_type === 'bank' 
                                                                ? 'border-teal-200 bg-teal-50 hover:bg-teal-100' 
                                                                : 'border-orange-200 bg-orange-50 hover:bg-orange-100'
                                                        }`}
                                                        whileHover={{ scale: 1.02 }}
                                                        whileTap={{ scale: 0.98 }}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                                                account.account_type === 'bank' 
                                                                    ? 'bg-teal-100' 
                                                                    : 'bg-orange-100'
                                                            }`}>
                                                                {account.account_type === 'bank' ? (
                                                                    <Building2 className="w-5 h-5 text-teal-600" />
                                                                ) : (
                                                                    <CreditCard className="w-5 h-5 text-orange-600" />
                                                                )}
                                                            </div>
                                                            <div className="text-left">
                                                                <p className="font-semibold text-gray-800">
                                                                    {account.account_type === 'bank' ? account.bank_name : account.network_name}
                                                                </p>
                                                                <p className="text-sm text-gray-600 capitalize">
                                                                    {account.account_type} Account
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </motion.button>
                                ))}
                            </div>
                                        </div>

                                        {/* Payment Details Form */}
                            <MemoizedPaymentDetailsForm
                                paymentDetails={paymentDetails}
                                onSubmit={handleSubmit}
                                onCancel={onCancel}
                                editing={editing}
                            />
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="events-view"
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        className="space-y-6"
                                    >
                                        {/* Events Grid */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                            {currentEvents && currentEvents.map((item, index) => (
                                                <motion.div
                                                    key={item.id}
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: index * 0.1 }}
                                                    onClick={() => initRequest(item)}
                                                    className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300 group"
                                                >
                                                    <div className="relative h-48 overflow-hidden">
                                                        <img 
                                                            src={domain + item.event.banner} 
                                                            alt={item.event['event-name']}
                                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                        />
                                                        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-all duration-300"></div>
                                                        <div className="absolute top-3 right-3">
                                                            <div className="bg-white/90 backdrop-blur-sm rounded-lg p-2">
                                                                <Eye className="w-4 h-4 text-gray-600" />
                        </div>
                                    </div>
                                </div>
                                                    
                                                    <div className="p-6">
                                                        <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2">
                                                            {item.event['event-name']}
                                                        </h3>
                                                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                                                            {item.event.description}
                                                        </p>
                                                        
                                                        <div className="space-y-3">
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-sm text-gray-500">Total Revenue</span>
                                                                <span className="font-bold text-orange-600">
                                                                    Ugx {item.amount.toLocaleString()}/=
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-sm text-gray-500">Amount Paid</span>
                                                                <span className="font-semibold text-teal-600">
                                                                    Ugx {item.total_paid ? item.total_paid.toLocaleString() : 0}/=
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-sm text-gray-500">Available</span>
                                                                <span className="font-semibold text-green-600">
                                                                    Ugx {(item.amount - (item.total_paid || 0)).toLocaleString()}/=
                                                                </span>
                                                            </div>
                        </div>
                                                        
                                                        <button className="w-full mt-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 !text-white px-4 py-3 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2">
                                                            <DollarSign className="w-4 h-4" />
                                                            Request Payment
                            </button>
                        </div>
                                                </motion.div>
                                            ))}
                    </div>

                                        {/* Pagination */}
                                        {totalPages > 1 && (
                                            <div className="flex items-center justify-center gap-2 mt-8">
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                                                    className="px-4 py-2 border border-orange-300 text-orange-600 rounded-lg hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                        >
                            Previous
                        </button>
                                                {[...Array(totalPages).keys()].map(page => {
                                                    const pageNum = page + 1;
                                                    const isCurrent = currentPage === pageNum;
                                                    return (
                            <button
                                                            key={pageNum}
                                                            onClick={() => handlePageChange(pageNum)}
                                                            className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                                                                isCurrent 
                                                                    ? 'bg-orange-500 !text-white' 
                                                                    : 'border border-orange-300 text-orange-600 hover:bg-orange-50'
                                                            }`}
                                                        >
                                                            {pageNum}
                            </button>
                                                    );
                                                })}
                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                                                    className="px-4 py-2 border border-orange-300 text-orange-600 rounded-lg hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                        >
                            Next
                        </button>
                    </div>
                )}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Sidebar */}
                        <div className="lg:col-span-1">
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sticky top-8"
                            >
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-teal-500 rounded-xl flex items-center justify-center">
                                        <Wallet className="w-5 h-5 !text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-800">Quick Actions</h3>
                                        <p className="text-sm text-gray-600">Manage your revenue</p>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <button 
                                        onClick={() => handleViewAccount(accountView)}
                                        className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 !text-white px-4 py-3 rounded-xl font-medium transition-all duration-200 flex items-center gap-3 mb-2"
                                    >
                                        {accountView ? (
                                            <>
                                                <Calendar className="w-5 h-5" />
                                                View Events
                                            </>
                                        ) : (
                                            <>
                                                <CreditCard className="w-5 h-5" />
                                                Manage Accounts
                                            </>
                                        )}
                                    </button>

                                    <div className="bg-gradient-to-r from-orange-50 to-teal-50 rounded-xl p-4 border border-orange-200">
                                        <h4 className="text-lg font-semibold text-gray-800 mb-3">Revenue Summary</h4>
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-gray-600">Total Events</span>
                                                <span className="font-semibold text-gray-800">{data?.length || 0}</span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-gray-600">Total Revenue</span>
                                                <span className="font-semibold text-orange-600">
                                                    Ugx {totalAmount?.toLocaleString() || 0}/=
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-gray-600">This Month</span>
                                                <span className="font-semibold text-teal-600">
                                                    {data?.filter(event => {
                                                        const eventDate = new Date(event.created_at);
                                                        const now = new Date();
                                                        return eventDate.getMonth() === now.getMonth() && 
                                                               eventDate.getFullYear() === now.getFullYear();
                                                    }).length || 0}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-gray-50 rounded-xl p-4">
                                        <h4 className="text-lg font-semibold text-gray-800 mb-3">Tips</h4>
                                        <div className="space-y-2 text-sm text-gray-600">
                                            <p>• Keep your account details updated</p>
                                            <p>• Request payments regularly</p>
                                            <p>• Monitor your revenue trends</p>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Payment Request Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton style={{backgroundColor: '#0d9488', color: 'white'}}>
                    <Modal.Title style={{ textTransform: "capitalize" }}>
                        {event && event.event['event-name']}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="space-y-4">
                        <div className="bg-gradient-to-r from-orange-50 to-teal-50 rounded-xl p-4 border border-orange-200">
                            <h5 className="font-bold text-orange-600 mb-2">Available Balance</h5>
                            <p className="text-sm font-bold text-gray-800">
                                Ugx {userBalance && userBalance.toLocaleString()}/=
                            </p>
                        </div>
                        
                        <div className="space-y-3">
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <span className="text-sm text-gray-600">Total Collected</span>
                                <span className="font-semibold text-gray-800">
                                    {event && event.amount.toLocaleString()}
                                </span>
                            </div>
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <span className="text-sm text-gray-600">Total Paid</span>
                                <span className="font-semibold text-gray-800">
                                    {event && event.total_paid > 0 ? event.total_paid.toLocaleString() : 0}
                                </span>
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <label htmlFor="amount" className="block text-sm font-semibold text-gray-700">
                                Enter Amount to Request
                            </label>
                            <input
                                type="number"
                                id="amount"
                                value={paymentRequestAmount}
                                onChange={(e) => setPaymentRequestAmount(Number(e.target.value))}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200"
                                placeholder="Enter amount..."
                            />
                        </div>
                        
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-sm text-blue-800">
                                <AlertCircle className="w-4 h-4 inline mr-2" />
                                Please ensure to enter correct information as we are not responsible for any errors.
                            </p>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" style={{borderColor: '#f97316', color: '#f97316'}} onClick={() => setShowModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" style={{backgroundColor: '#f97316', borderColor: '#f97316'}} onClick={handleRequest}>
                        Submit Payment Request
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default RevenueDashboard;